//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { IRootScopeService } from 'angular';
import React, { useEffect, useMemo, useState } from 'react';

import { IProduct } from '@models/IProduct';
import PartialCuid from '@models/PartialCuid';
import {
  IAssistedProductTaggingService,
  IProductTaggingService,
  IUniversalImageActionsService,
} from '@react-bridge/wrappers/ng-service-provider-types';
import gaApi from 'app/initialization/ga/ga-api';

import IntelligentProductTaggingInfoTip from './IntelligentProductTaggingInfoTip';
import ProductSuggestionBoundingBox, {
  BoundingBoxVisibilityState,
} from './ProductSuggestionBoundingBox';
import { IBoundingBoxSpatialData } from './ProductSuggestionBoundingBox';
import { TRACKING_CATEGORY } from '@tracker/trackerModel';

interface ISuggestionBoxClickParams {
  boxId: string;
  coordinates: IBoundingBoxSpatialData;
  event: IMouseOffsetEvent;
}

// This is needed to pass React mouse events where an Angular mouse event structure is expected
interface IMouseOffsetEvent {
  offsetX: number;
  offsetY: number;
}

export interface IBoundingBox {
  boxId: string;
  coordinates: IBoundingBoxSpatialData;
  selectedProduct?: IProduct;
  keywords?: string[];
}

interface IOwnProps {
  assistedProductTaggingService: IAssistedProductTaggingService;
  productTaggingService: IProductTaggingService;
  rootScope: IRootScopeService;
  suggestionBoxClicked: (params: ISuggestionBoxClickParams) => void;
  uiaService: IUniversalImageActionsService;
  universalItemId: PartialCuid;
}
type Props = IOwnProps;

// Initial delay where expanded tip should be collapsed when overlay is mounted
const INITIAL_IPT_INDICATORS_DELAY = 800;
// Duration where expanded IPT tip and all boxes should be displayed
const DISPLAY_IPT_INDICATORS_DURATION = 4000;
const MAX_NUM_VISIBLE_BOUNDING_BOXES = 3;

const getBoundingBoxArea = (box: IBoundingBox) =>
  box.coordinates.width * box.coordinates.height;

const ProductSuggestionOverlay: React.FunctionComponent<Props> = ({
  assistedProductTaggingService,
  productTaggingService,
  rootScope,
  suggestionBoxClicked,
  universalItemId,
  uiaService,
}) => {
  const [allBoundingBoxes, setAllBoundingBoxes] = useState<IBoundingBox[]>([]);
  const [existingProducts, setExistingProducts] = useState<IProduct[]>([]);
  const [
    shouldShowAllIptIndicators,
    setShouldShowAllIptIndicators,
  ] = useState<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      setShouldShowAllIptIndicators(true);
    }, INITIAL_IPT_INDICATORS_DELAY);
    setTimeout(() => {
      setShouldShowAllIptIndicators(false);
    }, DISPLAY_IPT_INDICATORS_DURATION);
  }, []);

  useEffect(() => {
    setExistingProducts(productTaggingService.getProducts());

    // Unregister this listener on cleanup
    return rootScope.$on('uia:products-changed', ($event, newProducts) => {
      setExistingProducts(newProducts);
    });
  }, []);

  useEffect(() => {
    const productSubscription = assistedProductTaggingService
      .getProductRelevancyDataSubject(universalItemId)
      .subscribe((newBoundingBoxes) => {
        // Sort by reverse size so smaller boxes render on top of larger boxes
        setAllBoundingBoxes(
          newBoundingBoxes.relevancyData.boundingBoxes.sort(
            (a, b) => getBoundingBoxArea(b) - getBoundingBoxArea(a),
          ),
        );
      });

    return () => {
      productSubscription.unsubscribe();
    };
  }, []);

  const currentBoundingBoxes = useMemo(() => {
    const existingProductStyleIds = existingProducts.map(
      (product) => product.productStyleId,
    );

    // Only show bounding boxes that have not been tagged with products
    return allBoundingBoxes.filter((boundingBox) => {
      return !existingProductStyleIds.includes(
        boundingBox?.selectedProduct?.productStyleId,
      );
    });
  }, [allBoundingBoxes, existingProducts]);

  const onSuggestionBoxClicked = (boxId: string, e: React.MouseEvent) => {
    const event = {
      offsetX: e.nativeEvent.offsetX,
      offsetY: e.nativeEvent.offsetY,
    };

    const cachedBoundingBox = assistedProductTaggingService.getCachedBoundingBox(
      universalItemId,
      boxId,
    );
    const coordinates = cachedBoundingBox.coordinates;
    const productCategory = cachedBoundingBox?.keywords[0];

    gaApi.recordEvent(
      getTrackingCategory(),
      gaApi.ACTION_ADD_PRODUCT_BOUNDING_BOX,
      productCategory,
    );

    suggestionBoxClicked({ boxId, coordinates, event });
  };

  const getTrackingCategory = () => {
    const currentPage = uiaService.getCurrentPage() ?? 'other';
    // This is the only remaining instance of a non-enumerable tracking category value. We'll cast it for backwards compat.
    return `${gaApi.CATEGORY_UNIVERSAL_IMAGE_ACTIONS}.${currentPage}` as TRACKING_CATEGORY;
  };

  const getVisibilityState = (boxIndex: number) => {
    if (shouldShowAllIptIndicators) {
      return BoundingBoxVisibilityState.HIGHLIGHTED;
    }

    if (boxIndex < MAX_NUM_VISIBLE_BOUNDING_BOXES) {
      return BoundingBoxVisibilityState.VISIBLE;
    }

    return BoundingBoxVisibilityState.HIDDEN;
  };

  const renderBoundingBoxes = () => {
    return currentBoundingBoxes.map((boundingBox, index) => (
      <ProductSuggestionBoundingBox
        key={boundingBox.boxId}
        boxId={boundingBox.boxId}
        coordinates={boundingBox.coordinates}
        visibilityState={getVisibilityState(index)}
        handleSuggestionBoxClicked={onSuggestionBoxClicked}
      />
    ));
  };

  return (
    <div>
      {currentBoundingBoxes.length !== 0 && (
        <div
          onMouseEnter={() => setShouldShowAllIptIndicators(true)}
          onMouseLeave={() => setShouldShowAllIptIndicators(false)}
        >
          <IntelligentProductTaggingInfoTip
            shouldDisplayExtendedTip={shouldShowAllIptIndicators}
          />
        </div>
      )}
      {renderBoundingBoxes()}
    </div>
  );
};

export default ProductSuggestionOverlay;
